import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import apiService from '../../../services/apiService';
import Select from 'react-select';
import { FaEdit, FaTrash, FaEye, FaDownload, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import { Country, State, City } from 'country-state-city';

const emptyForm = {
  farmerId: '',
  title: '',
  quantity: '',
  unit: 'kg',
  type: '',
  collectorId: '',
  status: '',
  description: '',
  deadline: '',
  estimatedValuePerUnit: '',
  totalEstimatedValue: '',
  attachments: [],
  location: {
    country: '',
    state: '',
    city: '',
    postalCode: '',
    address: '',
  },
};

function Waste() {
  const { isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const { data: requests, loading: requestsLoading, error: requestsError, fetchData: fetchRequests } = useApi('/waste');
  const { data: collectors, loading: collectorsLoading, error: collectorsError } = useApi('/users/userRole/waste_collector');
  const { data: farmers, loading: farmersLoading, error: farmersError } = useApi('/users/userRole/farmer');
  const { data: userDetails, loading: userDetailsLoading, error: userDetailsError, fetchData: fetchUserDetails } = useApi(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBidHistoryModalOpen, setIsBidHistoryModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [collectorDetails, setCollectorDetails] = useState(null);
  const [bidHistory, setBidHistory] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  useEffect(() => {
    setIsLoading(requestsLoading || collectorsLoading || farmersLoading);
    setCountries(Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    })));
  }, [requestsLoading, collectorsLoading, farmersLoading]);

  useEffect(() => {
    if (userDetails) setCollectorDetails(userDetails);
  }, [userDetails]);

  useEffect(() => {
    if (form.location?.country) {
      setStates(State.getStatesOfCountry(form.location.country).map((state) => ({
        value: state.isoCode,
        label: state.name,
      })));
      setCities([]);
    } else {
      setStates([]);
      setCities([]);
    }
  }, [form.location?.country]);

  useEffect(() => {
    if (form.location?.state && form.location?.country) {
      setCities(City.getCitiesOfState(form.location.country, form.location.state).map((city) => ({
        value: city.name,
        label: city.name,
      })));
    } else {
      setCities([]);
    }
  }, [form.location?.state, form.location?.country]);

  if (!isAdmin()) {
    toast.error('Access denied. Admins only.');
    return <Navigate to="/login" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const field = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        location: { ...prev.location, [field]: value },
      }));
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSelectChange = (name) => (selected) => {
    if (name === 'location.country') {
      setForm((prev) => ({
        ...prev,
        location: { ...prev.location, country: selected ? selected.value : '', state: '', city: '', address: '' },
      }));
    } else if (name === 'location.state') {
      setForm((prev) => ({
        ...prev,
        location: { ...prev.location, state: selected ? selected.value : '', city: '', address: '' },
      }));
    } else if (name === 'location.city') {
      setForm((prev) => ({
        ...prev,
        location: { ...prev.location, city: selected ? selected.value : '', address: '' },
      }));
    } else {
      setForm({ ...form, [name]: selected ? selected.value : '' });
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) {
      toast.error('No files selected');
      return;
    }
    const formData = new FormData();
    files.forEach((file) => formData.append('attachments', file));
    try {
      const response = await apiService.uploadFiles('/waste/upload', formData, { timeout: 30000 });
      if (response && response.urls) {
        const normalizedUrls = response.urls.map(url => url.startsWith('/') ? url : `/${url}`);
        setForm((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...normalizedUrls],
        }));
        toast.success('Files uploaded successfully');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Upload failed:', err.response?.config, err.message);
      toast.error(err.response?.data?.error || 'Failed to upload attachments');
    }
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setForm({
      farmerId: request.farmerId?._id || '',
      title: request.title || '',
      quantity: request.quantity || '',
      unit: request.unit || 'kg',
      type: request.type || '',
      collectorId: request.collectorId?._id || '',
      status: request.status || 'pending',
      description: request.description || '',
      deadline: request.deadline ? new Date(request.deadline).toISOString().split('T')[0] : '',
      estimatedValuePerUnit: request.estimatedValuePerUnit || '',
      totalEstimatedValue: request.totalEstimatedValue || '',
      attachments: request.attachments || [],
      location: {
        country: request.location?.country || '',
        state: request.location?.state || '',
        city: request.location?.city || '',
        postalCode: request.location?.postalCode || '',
        address: request.location?.address || '',
      },
    });
    setIsCreateMode(false);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setForm({ ...emptyForm, status: 'bidding' });
    setIsCreateMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this waste request?')) {
      try {
        await apiService.delete(`/waste/${requestId}`);
        toast.success('Waste request deleted successfully');
        fetchRequests();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to delete waste request');
      }
    }
  };

  const handleDeleteBid = async (requestId, bidId) => {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      try {
        await apiService.delete(`/waste/bid/${requestId}/${bidId}`);
        toast.success('Bid deleted successfully');
      } catch (err) {
        console.error('Delete bid error:', err);
      }
    }
  };

  const handleViewBids = (request) => {
    navigate(`/admin/bids/${request._id}`);
  };

  const handleViewCollector = async (collectorId) => {
    if (!collectorId) {
      toast.error('Invalid collector ID');
      return;
    }
    try {
      await fetchUserDetails(`/waste/collector-details/${collectorId}`);
      setIsDetailsModalOpen(true);
    } catch (err) {
      console.error('handleViewCollector error:', err);
    }
  };

  const handleViewBidHistory = async (collectorId) => {
    try {
      const response = await apiService.get(`/waste/collector-bids/${collectorId}`);
      setBidHistory(response.data);
      setIsBidHistoryModalOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to fetch bid history');
    }
  };

  const handleAcceptBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'accepted' });
      toast.success('Bid accepted successfully');
    } catch (err) {
      console.error('Accept bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to accept bid');
    }
  };

  const handleDeclineBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'declined' });
      toast.success('Bid declined successfully');
    } catch (err) {
      console.error('Decline bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to decline bid');
    }
  };

  const handlePendingBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'pending' });
      toast.success('Bid set to pending successfully');
    } catch (err) {
      console.error('Pending bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to set bid to pending');
    }
  };

  const handleCompleteBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'completed' });
      toast.success('Bid marked as completed successfully');
    } catch (err) {
      console.error('Complete bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to mark bid as completed');
    }
  };

  const handleCancelBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'canceled' });
      toast.success('Bid canceled successfully');
    } catch (err) {
      console.error('Cancel bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to cancel bid');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
    setIsCreateMode(false);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreateMode && (!form.farmerId || !form.title || !form.quantity || !form.unit || !form.type || !form.description || !form.deadline || !form.location.address)) {
      toast.error('Please fill in all required fields (farmer, title, quantity, unit, type, description, deadline, address)');
      return;
    }
    if (e.target.querySelector('input[type="file"]').files.length > 0 && form.attachments.length === 0) {
      toast.error('Please wait for file uploads to complete');
      return;
    }
    try {
      const payload = {
        farmerId: form.farmerId,
        collectorId: form.collectorId || undefined,
        title: form.title,
        quantity: Number(form.quantity),
        unit: form.unit,
        type: form.type,
        location: {
          coordinates: [],
          address: form.location.address,
          city: form.location.city,
          postalCode: form.location.postalCode,
          country: form.location.country,
          state: form.location.state,
        },
        description: form.description,
        deadline: new Date(form.deadline),
        estimatedValuePerUnit: form.estimatedValuePerUnit ? Number(form.estimatedValuePerUnit) : 0,
        totalEstimatedValue: form.totalEstimatedValue ? Number(form.totalEstimatedValue) : 0,
        attachments: form.attachments,
        status: form.collectorId ? 'accepted' : (form.status || 'bidding'),
      };

      if (isCreateMode) {
        await apiService.post('/waste', payload);
        toast.success('Waste request created successfully');
      } else {
        await apiService.put(`/waste/status/${editingRequest._id}`, payload);
        toast.success('Waste request updated successfully');
      }
      fetchRequests();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving waste request:', err.response?.data);
      toast.error(err.response?.data?.error || 'Failed to save waste request');
    }
  };

  const handleRemoveAttachment = (attachmentToRemove) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((attachment) => attachment !== attachmentToRemove),
    }));
  };

  const handleViewAttachment = (attachmentUrl) => {
    setSelectedAttachment(attachmentUrl);
    setIsAttachmentModalOpen(true);
  };

  const handleDownloadAttachment = async (attachmentUrl) => {
    try {
      const response = await apiService.get(`/waste/download/${encodeURIComponent(attachmentUrl)}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentUrl.split('/').pop() || 'attachment');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download attachment');
    }
  };

  const columns = [
    {
      key: 'farmerId',
      label: 'Farmer',
      render: (r) => `${r.farmerId?.firstName || ''} ${r.farmerId?.lastName || ''}` || 'N/A',
    },
    { key: 'title', label: 'Title', render: (r) => r.title || 'N/A' },
    { key: 'type', label: 'Type' },
    {
      key: 'quantity',
      label: 'Quantity',
      render: (r) => r.quantity ? `${r.quantity} ${r.unit || 'kg'}` : 'N/A',
    },
    {
      key: 'location.country',
      label: 'Country',
      render: (r) => r.location?.country || 'N/A',
    },
    {
      key: 'deadline',
      label: 'Deadline',
      render: (r) => r.deadline ? new Date(r.deadline).toLocaleDateString() : 'N/A',
    },
    {
      key: 'estimatedValuePerUnit',
      label: 'Value/Unit ($)',
      render: (r) => r.estimatedValuePerUnit || 'N/A',
    },
    {
      key: 'totalEstimatedValue',
      label: 'Total Value ($)',
      render: (r) => r.totalEstimatedValue || 'N/A',
    },
    {
      key: 'attachments',
      label: 'Attachments',
      render: (r) => (
        r.attachments?.length > 0 ? (
          <button
            onClick={() => handleViewAttachment(r.attachments[0])}
            className="text-blue-500 hover:text-blue-600"
            title="View Attachment"
          >
            <FaEye />
            {r.attachments.length > 1 && <span className="ml-1 text-sm">({r.attachments.length})</span>}
          </button>
        ) : 'No'
      ),
    },
    {
      key: 'collectorId',
      label: 'Collector',
      render: (r) => `${r.collectorId?.firstName || ''} ${r.collectorId?.lastName || ''}` || 'Unassigned',
    },
    { key: 'status', label: 'Status' },
    {
      key: 'bids',
      label: 'Bids',
      render: (r) => (
        <button
          onClick={() => handleViewBids(r)}
          className="text-blue-500 hover:text-blue-600"
          title="View Bids"
          aria-label="View bids"
        >
          <FaEye /> ({r.bids?.length || 0})
        </button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(r)}
            className="text-yellow-500 hover:text-yellow-600"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDelete(r._id)}
            className="text-red-500 hover:text-red-600"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  const bidHistoryColumns = [
    { key: 'requestId', label: 'Request ID' },
    { key: 'farmer', label: 'Farmer' },
    { key: 'quantity', label: 'Quantity (kg)' },
    { key: 'type', label: 'Type' },
    { key: 'requestStatus', label: 'Request Status' },
    { key: 'bidAmount', label: 'Bid Amount ($)' },
    { key: 'bidNote', label: 'Bid Note' },
    { key: 'bidStatus', label: 'Bid Status' },
    {
      key: 'bidCreatedAt',
      label: 'Bid Date',
      render: (b) => new Date(b.bidCreatedAt).toLocaleString(),
    },
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'bidding', label: 'Bidding' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'completed', label: 'Completed' },
    { value: 'canceled', label: 'Canceled' },
  ];

  const typeOptions = [
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'feed', label: 'Feed' },
    { value: 'energy', label: 'Energy' },
    { value: 'compost', label: 'Compost' },
    { value: 'biogas', label: 'Biogas' },
    { value: 'biomass', label: 'Biomass' },
    { value: 'recycling', label: 'Recycling' },
    { value: 'organic_waste', label: 'Organic Waste' },
    { value: 'poultry_litter', label: 'Poultry Litter' },
    { value: 'soil_conditioner', label: 'Soil Conditioner' },
    { value: 'manure_processing', label: 'Manure Processing' },
    { value: 'waste_to_energy', label: 'Waste to Energy' },
    { value: 'agricultural_reuse', label: 'Agricultural Reuse' },
    { value: 'feather_recycling', label: 'Feather Recycling' },
    { value: 'bedding_waste', label: 'Bedding Waste' },
    { value: 'industrial_recycling', label: 'Industrial Recycling' },
    { value: 'sustainable_disposal', label: 'Sustainable Disposal' },
  ];

  const collectorOptions = (collectors || []).map((c) => ({
    value: c._id,
    label: `${c.firstName} ${c.lastName}`,
  }));

  const farmerOptions = (farmers || []).map((f) => ({
    value: f._id,
    label: `${f.firstName} ${f.lastName}`,
  }));

  const renderAttachment = (attachmentUrl) => {
    const fullUrl = attachmentUrl.startsWith('http')
      ? attachmentUrl
      : `${process.env.REACT_APP_API_BASE_URL}/${attachmentUrl.replace(/^\/+/, '')}`;
    const ext = fullUrl.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return (
        <img
          src={fullUrl}
          alt="Attachment"
          className="w-full h-auto max-h-64 object-contain rounded-lg"
        />
      );
    } else if (ext === 'pdf') {
      return (
        <iframe
          src={`${fullUrl}#toolbar=0&navpanes=0`}
          title="Attachment Preview"
          className="w-full h-64 border rounded-lg"
          style={{ border: 'none' }}
        />
      );
    }
    return (
      <p className="text-gray-600 dark:text-gray-400">Unsupported file type</p>
    );
  };

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Waste Management</h2>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Request
        </button>
      </div>
      {isLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          Loading...
        </div>
      ) : requestsError || collectorsError || farmersError ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">
          Error: {requestsError || collectorsError || farmersError}
        </div>
      ) : !requests || requests.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No waste requests found.
        </div>
      ) : (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={columns} data={requests} />
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={isCreateMode ? 'Create Waste Request' : 'Update Waste Request'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Farmer (create only) */}
              {isCreateMode && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Farmer <span className="text-red-500">*</span></label>
                  <Select
                    options={farmerOptions}
                    value={farmerOptions.find((opt) => opt.value === form.farmerId) || null}
                    onChange={handleSelectChange('farmerId')}
                    placeholder="Select a farmer"
                    isClearable
                    className="mt-1"
                    classNamePrefix="react-select"
                    isDisabled={farmersLoading || !farmers || farmers.length === 0}
                  />
                </div>
              )}

              {/* Title */}
              <div className="lg:col-span-2">
                <FormInput
                  label="Title"
                  name="title"
                  type="text"
                  value={form.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter waste request title"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Quantity + Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity &amp; Unit <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    name="quantity"
                    type="number"
                    value={form.quantity}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter quantity"
                    className="flex-1 p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    min="0"
                  />
                  <select
                    name="unit"
                    value={form.unit}
                    onChange={handleInputChange}
                    required
                    className="w-24 p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="kg">kg</option>
                    <option value="ton">ton</option>
                  </select>
                </div>
              </div>

              {/* Type */}
              <div>
                <FormInput
                  label="Type"
                  name="type"
                  type="select"
                  value={form.type}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select type</option>
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FormInput>
              </div>

              {/* Collector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Collector</label>
                <Select
                  options={collectorOptions}
                  value={collectorOptions.find((opt) => opt.value === form.collectorId) || null}
                  onChange={handleSelectChange('collectorId')}
                  placeholder="Select a collector"
                  isClearable
                  className="mt-1"
                  classNamePrefix="react-select"
                  isDisabled={collectorsLoading || !collectors || collectors.length === 0}
                />
              </div>

              {/* Status */}
              <div>
                <FormInput
                  label="Status"
                  name="status"
                  type="select"
                  value={form.status}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select status</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FormInput>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                <Select
                  options={countries}
                  value={countries.find((c) => c.value === form.location.country) || null}
                  onChange={handleSelectChange('location.country')}
                  placeholder="Select country"
                  className="mt-1"
                  classNamePrefix="react-select"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                <Select
                  options={states}
                  value={states.find((s) => s.value === form.location.state) || null}
                  onChange={handleSelectChange('location.state')}
                  placeholder="Select state"
                  className="mt-1"
                  classNamePrefix="react-select"
                  isDisabled={!form.location.country}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                <Select
                  options={cities}
                  value={cities.find((c) => c.value === form.location.city) || null}
                  onChange={handleSelectChange('location.city')}
                  placeholder="Select city"
                  className="mt-1"
                  classNamePrefix="react-select"
                  isDisabled={!form.location.state}
                />
              </div>

              {/* Postal Code */}
              <div>
                <FormInput
                  label="Postal Code"
                  name="location.postalCode"
                  type="text"
                  value={form.location.postalCode}
                  onChange={handleInputChange}
                  placeholder="Enter postal code"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Address */}
              <div>
                <FormInput
                  label="Address"
                  name="location.address"
                  type="text"
                  value={form.location.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter address"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Description */}
              <div className="lg:col-span-2">
                <FormInput
                  label="Description"
                  name="description"
                  type="text"
                  value={form.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter description"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Deadline */}
              <div>
                <FormInput
                  label="Deadline"
                  name="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Estimated Value Per Unit */}
              <div>
                <FormInput
                  label="Estimated Value Per Unit ($)"
                  name="estimatedValuePerUnit"
                  type="number"
                  value={form.estimatedValuePerUnit}
                  onChange={handleInputChange}
                  placeholder="Enter value per unit"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>

              {/* Total Estimated Value */}
              <div>
                <FormInput
                  label="Total Estimated Value ($)"
                  name="totalEstimatedValue"
                  type="number"
                  value={form.totalEstimatedValue}
                  onChange={handleInputChange}
                  placeholder="Enter total estimated value"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>

              {/* Attachments */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
                {form.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.attachments.map((attachment, index) => (
                      <span key={index} className="inline-flex items-center bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-full text-sm">
                        {attachment.split('/').pop()}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(attachment)}
                          className="ml-2 text-red-500 hover:text-red-700"
                          title="Remove Attachment"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={isLoading}
              >
                {isCreateMode ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Collector Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setCollectorDetails(null);
        }}
        title="Collector Details"
        className="max-w-full lg:max-w-lg mx-4"
      >
        <div className="p-4">
          {userDetailsLoading ? (
            <p className="text-gray-600 dark:text-gray-300">Loading collector details...</p>
          ) : userDetailsError ? (
            <p className="text-red-600 dark:text-red-400">Error: {userDetailsError}</p>
          ) : collectorDetails ? (
            <div className="space-y-2 text-gray-900 dark:text-gray-100">
              <p><strong>Name:</strong> {collectorDetails.firstName} {collectorDetails.lastName}</p>
              <p><strong>Email:</strong> {collectorDetails.email}</p>
              <p><strong>Phone:</strong> {collectorDetails.phone}</p>
              <p><strong>Address:</strong> {collectorDetails.address}</p>
              <p><strong>Street:</strong> {collectorDetails.street}</p>
              <p><strong>Postal Code:</strong> {collectorDetails.postalCode}</p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">No collector details available.</p>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                setIsDetailsModalOpen(false);
                setCollectorDetails(null);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Bid History Modal */}
      <Modal
        isOpen={isBidHistoryModalOpen}
        onClose={() => {
          setIsBidHistoryModalOpen(false);
          setBidHistory(null);
        }}
        title={`Bid History for ${bidHistory?.collector?.firstName} ${bidHistory?.collector?.lastName}`}
        className="max-w-full lg:max-w-5xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          {bidHistory ? (
            <>
              <p className="mb-4 text-gray-900 dark:text-gray-100">
                <strong>Accepted Bids:</strong> {bidHistory.acceptedBidCount}
              </p>
              {bidHistory.bids.length > 0 ? (
                <Table columns={bidHistoryColumns} data={bidHistory.bids} />
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-300">
                  No bids found for this collector.
                </p>
              )}
            </>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">Loading bid history...</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => {
                setIsBidHistoryModalOpen(false);
                setBidHistory(null);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Attachment Preview Modal */}
      <Modal
        isOpen={isAttachmentModalOpen}
        onClose={() => {
          setIsAttachmentModalOpen(false);
          setSelectedAttachment(null);
        }}
        title="View Attachment"
        className="max-w-full lg:max-w-2xl mx-4"
      >
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {selectedAttachment ? (
            <div className="space-y-4 text-gray-900 dark:text-gray-100">
              {renderAttachment(selectedAttachment)}
              <p><strong>File:</strong> {selectedAttachment.split('/').pop()}</p>
              <button
                onClick={() => handleDownloadAttachment(selectedAttachment)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FaDownload /> Download
              </button>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">No attachment selected.</p>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setIsAttachmentModalOpen(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Waste;
